/**
 * Schema template library — 15 prebuilt JSON-LD shapes with form-driven
 * customization. Each template defines:
 *   - id / label / description (used in the picker UI)
 *   - category / icon (for grouping in the picker)
 *   - fields: a flat list of inputs to render; type 'array' nests itemFields
 *   - build(values): turns the flat values back into a real JSON-LD object
 *
 * Keeping values flat (instead of nested objects) means the form code stays
 * a simple key/value mapper — the build() function is the only place that
 * knows the shape of the final JSON-LD.
 */

const ctx = 'https://schema.org';

// Recursively strip empty strings / null / undefined / empty arrays so the
// generated JSON-LD only contains fields the user actually filled.
function clean(value) {
  if (Array.isArray(value)) {
    const out = value.map(clean).filter((v) => v !== undefined);
    return out.length ? out : undefined;
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const cleaned = clean(v);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return Object.keys(out).length ? out : undefined;
  }
  if (value === '' || value === null || value === undefined) return undefined;
  return value;
}

const postalAddress = (v) => ({
  '@type': 'PostalAddress',
  streetAddress: v.streetAddress,
  addressLocality: v.addressLocality,
  addressRegion: v.addressRegion,
  postalCode: v.postalCode,
  addressCountry: v.addressCountry,
});

const localBusinessFields = [
  { key: 'name', label: 'Business name', type: 'text', required: true, placeholder: 'Acme Wellness Spa' },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'url', label: 'Website URL', type: 'url', placeholder: 'https://example.com' },
  { key: 'telephone', label: 'Phone', type: 'tel', required: true, placeholder: '+1-555-123-4567' },
  { key: 'image', label: 'Image URL', type: 'url' },
  { key: 'streetAddress', label: 'Street address', type: 'text', required: true },
  { key: 'addressLocality', label: 'City', type: 'text', required: true },
  { key: 'addressRegion', label: 'State / region', type: 'text', required: true },
  { key: 'postalCode', label: 'Postal code', type: 'text', required: true },
  { key: 'addressCountry', label: 'Country (ISO)', type: 'text', required: true, default: 'US' },
  { key: 'priceRange', label: 'Price range', type: 'text', placeholder: '$$', help: 'Use $, $$, $$$ or $$$$' },
  { key: 'openingHours', label: 'Opening hours', type: 'text', placeholder: 'Mo-Fr 09:00-18:00', help: 'Schema.org format' },
];

const buildLocalBusinessLike = (type) => (v) =>
  clean({
    '@context': ctx,
    '@type': type,
    name: v.name,
    description: v.description,
    url: v.url,
    telephone: v.telephone,
    image: v.image,
    priceRange: v.priceRange,
    openingHours: v.openingHours,
    address: postalAddress(v),
  });

export const SCHEMA_TEMPLATES = [
  {
    id: 'LocalBusiness',
    label: 'Local business',
    description: 'Brick-and-mortar with NAP (Name / Address / Phone).',
    category: 'Local',
    icon: 'Building2',
    fields: localBusinessFields,
    build: buildLocalBusinessLike('LocalBusiness'),
  },
  {
    id: 'MedicalBusiness',
    label: 'Medical business',
    description: 'Clinics, hospitals, dental offices.',
    category: 'Local',
    icon: 'Stethoscope',
    fields: [
      ...localBusinessFields,
      {
        key: 'medicalSpecialty',
        label: 'Medical specialty',
        type: 'text',
        placeholder: 'Dentistry, Physiotherapy, …',
      },
    ],
    build: (v) =>
      clean({
        ...buildLocalBusinessLike('MedicalBusiness')(v),
        medicalSpecialty: v.medicalSpecialty,
      }),
  },
  {
    id: 'LegalService',
    label: 'Legal service',
    description: 'Law firms, solo practitioners, legal services.',
    category: 'Local',
    icon: 'Scale',
    fields: [
      ...localBusinessFields,
      { key: 'serviceType', label: 'Service type', type: 'text', placeholder: 'Immigration law' },
    ],
    build: (v) =>
      clean({
        ...buildLocalBusinessLike('LegalService')(v),
        serviceType: v.serviceType,
      }),
  },
  {
    id: 'FinancialService',
    label: 'Financial service',
    description: 'Banks, advisors, fintech.',
    category: 'Local',
    icon: 'Banknote',
    fields: [
      ...localBusinessFields,
      {
        key: 'feesAndCommissionsSpecification',
        label: 'Fees & commissions notes',
        type: 'textarea',
        placeholder: 'See https://example.com/pricing',
      },
    ],
    build: (v) =>
      clean({
        ...buildLocalBusinessLike('FinancialService')(v),
        feesAndCommissionsSpecification: v.feesAndCommissionsSpecification,
      }),
  },
  {
    id: 'Service',
    label: 'Service',
    description: 'Generic service offering (consulting, agency work).',
    category: 'Local',
    icon: 'Briefcase',
    fields: [
      { key: 'name', label: 'Service name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'providerName', label: 'Provider name', type: 'text', required: true },
      { key: 'providerUrl', label: 'Provider URL', type: 'url' },
      { key: 'areaServed', label: 'Area served', type: 'text', placeholder: 'United States' },
      { key: 'priceRange', label: 'Price range', type: 'text', placeholder: '$$' },
    ],
    build: (v) =>
      clean({
        '@context': ctx,
        '@type': 'Service',
        name: v.name,
        description: v.description,
        provider: {
          '@type': 'Organization',
          name: v.providerName,
          url: v.providerUrl,
        },
        areaServed: v.areaServed,
        priceRange: v.priceRange,
      }),
  },
  {
    id: 'Article',
    label: 'Article',
    description: 'Blog post, news article, long-form content.',
    category: 'Content',
    icon: 'FileText',
    fields: [
      { key: 'headline', label: 'Headline', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'authorName', label: 'Author name', type: 'text', required: true },
      { key: 'authorUrl', label: 'Author URL', type: 'url' },
      { key: 'datePublished', label: 'Published date', type: 'date', required: true },
      { key: 'dateModified', label: 'Modified date', type: 'date' },
      { key: 'image', label: 'Image URL', type: 'url' },
      { key: 'publisherName', label: 'Publisher name', type: 'text', required: true },
      { key: 'publisherLogo', label: 'Publisher logo URL', type: 'url' },
    ],
    build: (v) =>
      clean({
        '@context': ctx,
        '@type': 'Article',
        headline: v.headline,
        description: v.description,
        image: v.image,
        author: { '@type': 'Person', name: v.authorName, url: v.authorUrl },
        datePublished: v.datePublished,
        dateModified: v.dateModified,
        publisher: {
          '@type': 'Organization',
          name: v.publisherName,
          logo: v.publisherLogo
            ? { '@type': 'ImageObject', url: v.publisherLogo }
            : undefined,
        },
      }),
  },
  {
    id: 'FAQPage',
    label: 'FAQ page',
    description: 'A list of question / answer pairs — high signal for AI engines.',
    category: 'Content',
    icon: 'HelpCircle',
    fields: [
      {
        key: 'mainEntity',
        label: 'Questions',
        type: 'array',
        required: true,
        itemFields: [
          { key: 'question', label: 'Question', type: 'text', required: true },
          { key: 'answer', label: 'Answer', type: 'textarea', required: true },
        ],
      },
    ],
    build: (v) =>
      clean({
        '@context': ctx,
        '@type': 'FAQPage',
        mainEntity: (v.mainEntity || []).map((q) => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: { '@type': 'Answer', text: q.answer },
        })),
      }),
  },
  {
    id: 'BreadcrumbList',
    label: 'Breadcrumb list',
    description: 'Page hierarchy crumbs (Home → Category → Page).',
    category: 'Content',
    icon: 'ChevronRight',
    fields: [
      {
        key: 'items',
        label: 'Crumbs',
        type: 'array',
        required: true,
        itemFields: [
          { key: 'name', label: 'Crumb name', type: 'text', required: true },
          { key: 'item', label: 'URL', type: 'url', required: true },
        ],
      },
    ],
    build: (v) =>
      clean({
        '@context': ctx,
        '@type': 'BreadcrumbList',
        itemListElement: (v.items || []).map((it, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          name: it.name,
          item: it.item,
        })),
      }),
  },
  {
    id: 'Review',
    label: 'Review',
    description: 'A single review with rating and body.',
    category: 'Reputation',
    icon: 'Star',
    fields: [
      { key: 'itemName', label: 'Item / business reviewed', type: 'text', required: true },
      { key: 'ratingValue', label: 'Rating value (0-5)', type: 'number', required: true, default: 5 },
      { key: 'bestRating', label: 'Best rating', type: 'number', default: 5 },
      { key: 'reviewBody', label: 'Review body', type: 'textarea' },
      { key: 'authorName', label: 'Reviewer name', type: 'text', required: true },
      { key: 'datePublished', label: 'Date', type: 'date' },
    ],
    build: (v) =>
      clean({
        '@context': ctx,
        '@type': 'Review',
        itemReviewed: { '@type': 'Thing', name: v.itemName },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: v.ratingValue,
          bestRating: v.bestRating,
        },
        reviewBody: v.reviewBody,
        author: { '@type': 'Person', name: v.authorName },
        datePublished: v.datePublished,
      }),
  },
  {
    id: 'AggregateRating',
    label: 'Aggregate rating',
    description: 'Summary of many reviews (avg rating + count).',
    category: 'Reputation',
    icon: 'Trophy',
    fields: [
      { key: 'itemName', label: 'Item / business', type: 'text', required: true },
      { key: 'ratingValue', label: 'Average rating', type: 'number', required: true, default: 4.8 },
      { key: 'reviewCount', label: 'Number of reviews', type: 'number', required: true, default: 100 },
      { key: 'bestRating', label: 'Best rating', type: 'number', default: 5 },
    ],
    build: (v) =>
      clean({
        '@context': ctx,
        '@type': 'AggregateRating',
        itemReviewed: { '@type': 'Thing', name: v.itemName },
        ratingValue: v.ratingValue,
        reviewCount: v.reviewCount,
        bestRating: v.bestRating,
      }),
  },
  {
    id: 'Organization',
    label: 'Organization',
    description: 'Your brand / company entity (helps AI engines understand who you are).',
    category: 'Entity',
    icon: 'Building',
    fields: [
      { key: 'name', label: 'Organization name', type: 'text', required: true },
      { key: 'url', label: 'Website URL', type: 'url', required: true },
      { key: 'logo', label: 'Logo URL', type: 'url' },
      { key: 'description', label: 'Description', type: 'textarea' },
      {
        key: 'sameAs',
        label: 'Social URLs',
        type: 'array',
        itemFields: [
          { key: 'url', label: 'URL', type: 'url', required: true, placeholder: 'https://twitter.com/acme' },
        ],
      },
    ],
    build: (v) =>
      clean({
        '@context': ctx,
        '@type': 'Organization',
        name: v.name,
        url: v.url,
        logo: v.logo,
        description: v.description,
        sameAs: (v.sameAs || []).map((s) => s.url).filter(Boolean),
      }),
  },
  {
    id: 'Person',
    label: 'Person',
    description: 'Author / founder / public profile.',
    category: 'Entity',
    icon: 'User',
    fields: [
      { key: 'name', label: 'Full name', type: 'text', required: true },
      { key: 'jobTitle', label: 'Job title', type: 'text' },
      { key: 'url', label: 'Profile URL', type: 'url' },
      { key: 'image', label: 'Photo URL', type: 'url' },
      { key: 'worksFor', label: 'Employer', type: 'text' },
      {
        key: 'sameAs',
        label: 'Social URLs',
        type: 'array',
        itemFields: [{ key: 'url', label: 'URL', type: 'url', required: true }],
      },
    ],
    build: (v) =>
      clean({
        '@context': ctx,
        '@type': 'Person',
        name: v.name,
        jobTitle: v.jobTitle,
        url: v.url,
        image: v.image,
        worksFor: v.worksFor ? { '@type': 'Organization', name: v.worksFor } : undefined,
        sameAs: (v.sameAs || []).map((s) => s.url).filter(Boolean),
      }),
  },
  {
    id: 'Event',
    label: 'Event',
    description: 'Conference, webinar, workshop.',
    category: 'Commerce',
    icon: 'Calendar',
    fields: [
      { key: 'name', label: 'Event name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'startDate', label: 'Start (date + time)', type: 'datetime-local', required: true },
      { key: 'endDate', label: 'End (date + time)', type: 'datetime-local' },
      { key: 'locationName', label: 'Venue name', type: 'text', required: true },
      { key: 'locationAddress', label: 'Venue address', type: 'text' },
      { key: 'organizerName', label: 'Organizer name', type: 'text' },
      { key: 'organizerUrl', label: 'Organizer URL', type: 'url' },
      {
        key: 'eventStatus',
        label: 'Status',
        type: 'select',
        default: 'EventScheduled',
        options: [
          { value: 'EventScheduled', label: 'Scheduled' },
          { value: 'EventRescheduled', label: 'Rescheduled' },
          { value: 'EventPostponed', label: 'Postponed' },
          { value: 'EventCancelled', label: 'Cancelled' },
          { value: 'EventMovedOnline', label: 'Moved online' },
        ],
      },
    ],
    build: (v) =>
      clean({
        '@context': ctx,
        '@type': 'Event',
        name: v.name,
        description: v.description,
        startDate: v.startDate,
        endDate: v.endDate,
        eventStatus: v.eventStatus
          ? `https://schema.org/${v.eventStatus}`
          : undefined,
        location: {
          '@type': 'Place',
          name: v.locationName,
          address: v.locationAddress,
        },
        organizer: v.organizerName
          ? {
              '@type': 'Organization',
              name: v.organizerName,
              url: v.organizerUrl,
            }
          : undefined,
      }),
  },
  {
    id: 'Product',
    label: 'Product',
    description: 'Physical or digital product with price.',
    category: 'Commerce',
    icon: 'Package',
    fields: [
      { key: 'name', label: 'Product name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'image', label: 'Image URL', type: 'url' },
      { key: 'brand', label: 'Brand', type: 'text' },
      { key: 'sku', label: 'SKU', type: 'text' },
      { key: 'price', label: 'Price', type: 'number', required: true },
      { key: 'currency', label: 'Currency (ISO)', type: 'text', required: true, default: 'USD' },
      {
        key: 'availability',
        label: 'Availability',
        type: 'select',
        default: 'InStock',
        options: [
          { value: 'InStock', label: 'In stock' },
          { value: 'OutOfStock', label: 'Out of stock' },
          { value: 'PreOrder', label: 'Pre-order' },
          { value: 'Discontinued', label: 'Discontinued' },
        ],
      },
    ],
    build: (v) =>
      clean({
        '@context': ctx,
        '@type': 'Product',
        name: v.name,
        description: v.description,
        image: v.image,
        sku: v.sku,
        brand: v.brand ? { '@type': 'Brand', name: v.brand } : undefined,
        offers: {
          '@type': 'Offer',
          price: v.price,
          priceCurrency: v.currency,
          availability: v.availability
            ? `https://schema.org/${v.availability}`
            : undefined,
        },
      }),
  },
  {
    id: 'SoftwareApplication',
    label: 'Software application',
    description: 'SaaS, mobile/desktop apps, browser extensions.',
    category: 'Commerce',
    icon: 'Code2',
    fields: [
      { key: 'name', label: 'App name', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      {
        key: 'applicationCategory',
        label: 'Category',
        type: 'text',
        placeholder: 'BusinessApplication',
      },
      {
        key: 'operatingSystem',
        label: 'Operating system',
        type: 'text',
        placeholder: 'Web, iOS, Android, macOS, Windows',
      },
      { key: 'price', label: 'Price', type: 'number' },
      { key: 'currency', label: 'Currency (ISO)', type: 'text', default: 'USD' },
      { key: 'downloadUrl', label: 'Download URL', type: 'url' },
      { key: 'ratingValue', label: 'Average rating', type: 'number' },
      { key: 'ratingCount', label: 'Rating count', type: 'number' },
    ],
    build: (v) =>
      clean({
        '@context': ctx,
        '@type': 'SoftwareApplication',
        name: v.name,
        description: v.description,
        applicationCategory: v.applicationCategory,
        operatingSystem: v.operatingSystem,
        downloadUrl: v.downloadUrl,
        offers:
          v.price != null && v.price !== ''
            ? {
                '@type': 'Offer',
                price: v.price,
                priceCurrency: v.currency,
              }
            : undefined,
        aggregateRating:
          v.ratingValue && v.ratingCount
            ? {
                '@type': 'AggregateRating',
                ratingValue: v.ratingValue,
                ratingCount: v.ratingCount,
              }
            : undefined,
      }),
  },
];

export const CATEGORY_ORDER = ['Local', 'Content', 'Reputation', 'Entity', 'Commerce'];

export function getTemplate(id) {
  return SCHEMA_TEMPLATES.find((t) => t.id === id);
}

// Build default form values from a template's field defaults.
export function defaultValues(template) {
  const out = {};
  for (const f of template.fields) {
    if (f.type === 'array') {
      out[f.key] = [];
    } else if (f.default !== undefined) {
      out[f.key] = f.default;
    } else {
      out[f.key] = '';
    }
  }
  return out;
}

// Validate that required fields are filled. Returns a list of error messages.
export function validateValues(template, values) {
  const errors = [];
  for (const f of template.fields) {
    if (!f.required) continue;
    const v = values[f.key];
    if (f.type === 'array') {
      if (!Array.isArray(v) || v.length === 0) {
        errors.push(`${f.label} requires at least one entry.`);
        continue;
      }
      for (let i = 0; i < v.length; i++) {
        for (const sub of f.itemFields || []) {
          if (sub.required && !v[i]?.[sub.key]) {
            errors.push(`${f.label} #${i + 1}: ${sub.label} is required.`);
          }
        }
      }
    } else if (v === '' || v === null || v === undefined) {
      errors.push(`${f.label} is required.`);
    }
  }
  return errors;
}
